import React, { useState } from "react";
import clsx from "clsx";
import { getTailwindColorClass, Intent } from "lib/common/enums";
import { ErrorIcon, EyeIcon, InfoIcon } from "../Icon";

export interface TextFieldProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  label?: string;
  placeholder?: string;
  required?: boolean;
  icon?: React.ReactNode;
  labelClass?: string;
  intent?: Intent;
  customIntent?: Intent;
  customMessage?: string;
  message?: string;
  type?: string;
  inputStyle?: string;
  infoIcon?: boolean;
  stateChange?: (e: any) => void;
  defaultValue?: string;
  focusColor?: string;
}

// eslint-disable-next-line react/display-name
const TextField = React.forwardRef(
  (
    {
      value,
      onChange,
      onBlur,
      label,
      placeholder,
      icon,
      className,
      required = false,
      labelClass,
      intent,
      message,
      type = "text",
      inputStyle,
      stateChange,
      customIntent,
      customMessage,
      defaultValue,
      infoIcon = false,
      focusColor = "border-primary-600",
      ...rest
    }: TextFieldProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const [focus, setFocus] = useState(false);
    const [blurred, setBlurred] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const borderColor = customIntent
      ? getTailwindColorClass(customIntent)
      : intent && getTailwindColorClass(intent);

    return (
      <>
        <label
          className={clsx(
            "flex flex-col text-md text-gray-900 dark:text-gray-100 font-bold",
            labelClass
          )}
        >
          {label && label.length > 0 && (
            <span className="mb-1 font-medium">
              {label}
              {required && <span className="text-red-600 ml-1">*</span>}
              {infoIcon && (
                <InfoIcon
                  className="ml-1 inline-block"
                  size={4}
                  viewBox="0 0 16 16"
                />
              )}
            </span>
          )}
          <div
            className={clsx(
              className,
              `border px-2 ${focus && focusColor} border-${
                !className?.includes("border-") && borderColor === "gray"
                  ? "gray-400"
                  : borderColor
              } dark:border-gray-600 h-11 text-sm text-gray-900 dark:text-gray-100 transition-all flex flex-row items-center`
            )}
          >
            {icon}
            <input
              ref={ref}
              value={value}
              onChange={(e) => {
                onChange && onChange(e);
                stateChange && stateChange(e);
              }}
              type={type === "password" && showPassword ? "text" : type}
              placeholder={focus ? "" : placeholder}
              onFocus={() => setFocus(true)}
              onBlur={(e) => {
                onBlur && onBlur(e);
                setFocus(false);
                setBlurred(true);
              }}
              required={required}
              className={clsx(
                blurred && "input_blurred",
                `outline-none align-middle w-full bg-transparent border-0 focus:ring-0 text-base`,
                inputStyle
              )}
              {...rest}
            />
            {intent === "ERROR" && type !== "password" && <ErrorIcon />}
            {type === "password" && (
              <span
                className="cursor-pointer z-10 my-0 align-middle"
                onClick={() => setShowPassword(!showPassword)}
              >
                <EyeIcon fill={showPassword ? "#137ACD" : "#91A4B7"} />
              </span>
            )}
          </div>

          <span className={`font-normal text-sm text-${borderColor}`}>
            {message && message}
            {customMessage && customMessage}
          </span>
        </label>
      </>
    );
  }
);

export default TextField;
